import { CheckIcon, PlusIcon } from "lucide-react";
import {
  Comedian,
  getComedianByIDOrThrow,
  isPlaceholderComedian,
} from "@/data/comedians";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import {
  FeedEvents,
  FollowComedianEvents,
  trackEvent,
} from "@punchup/analytics";
import { cn, getComedianInitials } from "@/components/common/utils";
import { startTransition, useCallback, useState } from "react";
import { usePathname, useRouter } from "next/navigation";

import { CheckoutModalOrDrawer } from "@punchup/ui-common/components/checkout";
import { ComedianName } from "@punchup/venues/client";
import Link from "next/link";
import { Loader } from "@/components/common/icons";
import { SuggestedComedianAvatar } from "@punchup/ui-common/components/signin";
import { logException } from "@/analytics/sentry";
import { toast } from "@/shadcn/ui/use-toast";
import { useFollowComedian } from "@/hooks/follows/useFollowComedian";
import { useGate } from "statsig-react";
import { useSession } from "@/hooks";
import { useSubscriptions } from "@/hooks/useSubscriptions";

export default function ComedianAvatarWithFollowCircle({
  comedianId,
  comedianImgUrl,
  comedianName,
  comedianSlug,
  isNotPunchupComedian = false,
  hideComedianName = false,
  hideFollowIndicator = false,
  size = "sm",
  isFollowed = false,
  className,
}: {
  comedianId: string;
  comedianImgUrl: string | undefined;
  comedianName: string;
  comedianSlug?: string;
  isNotPunchupComedian?: boolean;
  hideComedianName?: boolean;
  hideFollowIndicator?: boolean;
  size?: "sm" | "md" | "lg" | number;
  isFollowed?: boolean;
  className?: string;
  alwaysShowPlusIcon?: boolean;
}) {
  const [comedianToFollow, setComedianToFollow] = useState<Comedian | null>(
    null
  );
  const [displayFollowComedianModal, setDisplayFollowComedianModal] =
    useState<boolean>(false);
  const session = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const { refetch: refetchSubscriptions } = useSubscriptions();
  const { followComedianAsync, isFollowing: isLoading } = useFollowComedian();

  // Feature gate for new avatar click behavior
  const { value: separateAvatarClickEnabled } = useGate(
    "separate_avatar_follow_click"
  );

  const isPunchupComedian =
    !isNotPunchupComedian && !isPlaceholderComedian(comedianName);

  const handleFollowComedian = useCallback(async () => {
    if (!isPunchupComedian) return;

    trackEvent(FollowComedianEvents.SUGGESTED, {
      followLocation: FOLLOW_LOCATIONS.SUGGESTED,
      comedianId,
      comedianName,
    });

    // If the user is not signed in, show the sign in modal
    if (!session?.user) {
      setDisplayFollowComedianModal(true);
      const comedian = await getComedianByIDOrThrow(comedianId);
      setComedianToFollow(comedian);
      return;
    }

    // Else, follow the comedian via the hook (which uses the v2 API)
    try {
      await followComedianAsync({
        comedian: { id: comedianId },
        followLocation: FOLLOW_LOCATIONS.SUGGESTED,
      });
      refetchSubscriptions();
      toast({
        title: `Successfully subscribed to ${comedianName}`,
        variant: "success",
      });
      startTransition(() => {
        router.refresh();
      });
    } catch (err) {
      logException(err);
    }
  }, [
    isPunchupComedian,
    session?.user,
    comedianId,
    comedianName,
    followComedianAsync,
    refetchSubscriptions,
    router,
  ]);

  const iconClassName = cn(
    "w-[18px] h-[18px] text-white",
    size === "sm" && "w-[12px] h-[12px]",
    size === "md" && "w-[16px] h-[16px]",
    size === "lg" && "w-[20px] h-[20px]"
  );

  const followIndicatorClassName = cn(
    "absolute bottom-[1px] right-[1px] bg-primary rounded-full w-[26px] h-[26px] flex items-center justify-center",
    size === "sm" && "w-[20px] h-[20px]",
    size === "md" && "w-[23px] h-[23px]",
    size === "lg" && "w-[26px] h-[26px]"
  );

  // New behavior: separate avatar and follow button clicks
  const renderWithSeparateClicks = () => {
    if (!isPunchupComedian) {
      return (
        <SuggestedComedianAvatar
          fallbackText={getComedianInitials(comedianName)}
          imageSrc={comedianImgUrl}
          displayName={comedianName}
          isNotPunchupComedian={isNotPunchupComedian}
          isFollowed={isFollowed}
          size={size}
        />
      );
    }

    const avatarElement = (
      <SuggestedComedianAvatar
        fallbackText={getComedianInitials(comedianName)}
        imageSrc={comedianImgUrl}
        displayName={comedianName}
        isNotPunchupComedian={isNotPunchupComedian}
        isFollowed={isFollowed}
        size={size}
      />
    );

    return (
      <div className="relative rounded-full">
        {comedianSlug ? (
          <Link href={`/${comedianSlug}`} className="block">
            {avatarElement}
          </Link>
        ) : (
          avatarElement
        )}
        {!hideFollowIndicator && (
          <button
            type="button"
            disabled={isFollowed || isLoading}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFollowComedian();
            }}
            className={cn(
              followIndicatorClassName,
              "cursor-pointer hover:scale-110 transition-transform"
            )}
          >
            {isLoading ? (
              <Loader />
            ) : isFollowed ? (
              <CheckIcon className={iconClassName} />
            ) : (
              <PlusIcon className={iconClassName} />
            )}
          </button>
        )}
      </div>
    );
  };

  // Legacy behavior: clicking anywhere on avatar triggers follow
  const renderLegacy = () => {
    if (!isPunchupComedian) {
      return (
        <SuggestedComedianAvatar
          fallbackText={getComedianInitials(comedianName)}
          imageSrc={comedianImgUrl}
          displayName={comedianName}
          isNotPunchupComedian={isNotPunchupComedian}
          isFollowed={isFollowed}
          size={size}
        />
      );
    }

    return (
      <button
        type="button"
        disabled={isFollowed || isLoading}
        onClick={handleFollowComedian}
        className="group relative rounded-full"
      >
        <SuggestedComedianAvatar
          fallbackText={getComedianInitials(comedianName)}
          imageSrc={comedianImgUrl}
          displayName={comedianName}
          isNotPunchupComedian={isNotPunchupComedian}
          isFollowed={isFollowed}
          size={size}
        />
        {!hideFollowIndicator && (
          <>
            {isLoading ? (
              <div className={followIndicatorClassName}>
                <Loader />
              </div>
            ) : isFollowed ? (
              <div className={followIndicatorClassName}>
                <CheckIcon className={iconClassName} />
              </div>
            ) : (
              <div className={followIndicatorClassName}>
                <PlusIcon className={iconClassName} />
              </div>
            )}
          </>
        )}
      </button>
    );
  };

  return (
    <>
      <span className={cn("flex items-center gap-x-6", className)}>
        {separateAvatarClickEnabled
          ? renderWithSeparateClicks()
          : renderLegacy()}
        <ComedianName
          comedianName={comedianName}
          size={size}
          hide={hideComedianName}
          className="pl-2 md:pl-4"
        />
      </span>
      {comedianToFollow && (
        <CheckoutModalOrDrawer
          isOpen={displayFollowComedianModal}
          setIsOpen={setDisplayFollowComedianModal}
          comedianToFollow={comedianToFollow}
          redirectTo={pathname}
          followLocation={FOLLOW_LOCATIONS.SUGGESTED}
          onOpen={(params) => {
            trackEvent(FeedEvents.OPENED_SUBSCRIPTION_MODAL, params);
          }}
        />
      )}
    </>
  );
}
